/** Tutarlar decimal string; toplamlar sunucuda hesaplanır. */
export type Money = string

export const STAGES = ['lead', 'demo', 'proposal', 'customer', 'lost'] as const
export type Stage = (typeof STAGES)[number]

export const DELIVERY = ['none', 'planned', 'installing', 'live'] as const
export type Delivery = (typeof DELIVERY)[number]

export const CHANNELS = ['visit', 'phone', 'online', 'email'] as const
export type Channel = (typeof CHANNELS)[number]

export const RESULTS = ['reached', 'not_reached', 'call_again'] as const
export type MeetingResult = (typeof RESULTS)[number]

export const OFFER_STATUSES = ['draft', 'sent', 'accepted', 'rejected'] as const
export type OfferStatus = (typeof OFFER_STATUSES)[number]

export const MODULE_STATUSES = ['active', 'trial', 'planned', 'cancelled'] as const
export type ModuleStatus = (typeof MODULE_STATUSES)[number]

export const WORK_KINDS = ['demo', 'installation', 'training'] as const
export type WorkKind = (typeof WORK_KINDS)[number]
export const WORK_STATUSES = ['planned', 'in_progress', 'done', 'cancelled'] as const
export type WorkStatus = (typeof WORK_STATUSES)[number]

export const REQUEST_STATUSES = ['open', 'in_progress', 'resolved'] as const
export type RequestStatus = (typeof REQUEST_STATUSES)[number]
export const PRIORITIES = ['low', 'medium', 'high'] as const
export type Priority = (typeof PRIORITIES)[number]

export const FILE_CATEGORIES = ['contract', 'invoice', 'receipt', 'other'] as const
export type FileCategory = (typeof FILE_CATEGORIES)[number]

export const UNLINK_REASONS = ['wrong_customer', 'wrong_project', 'duplicate', 'other'] as const
export type UnlinkReason = (typeof UNLINK_REASONS)[number]

export const TEAM_ROLES = ['owner', 'installation', 'support'] as const
export type TeamRole = (typeof TEAM_ROLES)[number]

export const ORDER_STATUSES = ['pending', 'approved', 'rejected'] as const
export type OrderStatus = (typeof ORDER_STATUSES)[number]
export const LICENSE_TERMS = ['monthly', 'yearly', 'perpetual'] as const
export type LicenseTerm = (typeof LICENSE_TERMS)[number]

export const RENEWAL_KINDS = ['license', 'maintenance', 'support'] as const
export type RenewalKind = (typeof RENEWAL_KINDS)[number]
export const RENEWAL_STATUSES = ['upcoming', 'contacted', 'renewed', 'cancelled'] as const
export type RenewalStatus = (typeof RENEWAL_STATUSES)[number]

export const TRIAL_STATUSES = ['active', 'expired', 'converted', 'cancelled'] as const
export type TrialStatus = (typeof TRIAL_STATUSES)[number]

export type CollectionStatus = 'pending' | 'partial' | 'paid' | 'overdue'

export type Staff = { id: string; name: string; title: string }
export type CrmProject = { id: string; name: string }

export type Customer = {
  id: string
  name: string
  legalName: string
  sector: string
  sectorId: string
  projectId: string
  stage: Stage
  delivery: Delivery
  contactName: string
  contactTitle: string
  phone: string
  email: string
  city: string
  district: string
  region: string
  taxNo: string
  taxOffice: string
  website: string
  /** Canlı ortam: müşterinin kullandığı uygulama (site) ve yönetim paneli adresleri. */
  appUrl: string
  adminUrl: string
  address: string
  ownerId: string
  /** Firma sahibinin tanımladığı kategori (Sıcak, İlgisiz, İstemiyor vb.); boşsa atanmamış. */
  categoryId: string
  team: { userId: string; role: TeamRole }[]
  createdAt: string
  /** Sunucu hesaplar. */
  lastMeeting: string | null
  nextAction: { date: string; text: string; overdue: boolean } | null
  balance: Money
}

export type Expense = { id: string; title: string; amount: Money; date: string; fileName: string }
export type InternalNote = { id: string; author: string; at: string; body: string }
export type HistoryEntry = { id: string; at: string; by: string; text: string }

/** Toplantı notu: yapılacaklar listesi mantığında tek satırlık madde. */
export type MinuteItem = { id: string; text: string; priority: Priority; done: boolean; createdBy: string; createdAt: string }

export type Meeting = {
  id: string
  customerId: string
  date: string
  start: string
  end: string
  staffId: string
  /** Tutanağı yalnız sahip, katılımcı personel veya not yöneticisi değiştirebilir. */
  ownerId: string
  /** Katılan personel (birden çok olabilir) ve müşteri tarafı katılımcılar. */
  staffIds: string[]
  contactIds: string[]
  /** Müşterinin görüşmede verdiği cevap ve alınan kararlar. */
  customerResponse: string
  decisions: string
  channel: Channel
  result: MeetingResult
  summary: string
  nextAction: { date: string; staffId: string; text: string } | null
  expenses: Expense[]
  notes: InternalNote[]
  history: HistoryEntry[]
  minutes: MinuteItem[]
  /** Yapılandırılmış tutanak: gündem maddesi, görüşülenler, karar. */
  agenda: AgendaItem[]
  /** Kişi kartı olmayan dış katılımcılar. */
  guests: Guest[]
  /** Çevrimiçi toplantı; şifre yalnız istenince (denetim kaydıyla) açılır. */
  online: { platform: OnlinePlatform | ''; url: string; code: string; hasPasscode: boolean }
  /** Not görünürlüğü: müşteriyi görebilen herkes / katılımcılar ve yetkililer / gizli. */
  visibility: MeetingVisibility
  /** İçerik bu kullanıcıdan gizlendi (yalnız varlığı, tarihi ve personeli görünür). */
  restricted: boolean
}

export const MEETING_VISIBILITIES = ['restricted', 'private', 'account'] as const
export type MeetingVisibility = (typeof MEETING_VISIBILITIES)[number]

export type AgendaItem = { title: string; notes: string; decision: string }
export type Guest = { name: string; email: string; company: string; role: string }
export const ONLINE_PLATFORMS = ['zoom', 'teams', 'meet', 'webex', 'other'] as const
export type OnlinePlatform = (typeof ONLINE_PLATFORMS)[number]

/** Kalem: proje modülü (module/feature), ek iş (extra, açıklamalı) ya da eski serbest kalem (deliverable). */
export type OfferItem = { id: string; name: string; qty: number; unitPrice: Money; lineType: string; refCode: string; description: string; discount: Money }

export const QUOTE_FLOW_STATUSES = ['taslak', 'manager_review', 'owner_review', 'approved', 'sent', 'negotiation', 'final', 'accepted', 'rejected'] as const
export type QuoteFlowStatus = (typeof QUOTE_FLOW_STATUSES)[number]
export type Offer = {
  id: string
  customerId: string
  number: string
  date: string
  validUntil: string
  status: OfferStatus
  items: OfferItem[]
  discountPct: number
  note: string
  /** Sunucu hesaplar. */
  subtotal: Money
  discount: Money
  total: Money
  /** Kalem id → adet × birim fiyat. */
  lineTotals: Record<string, Money>
  /** Taslak/gönderilmiş ve geçerlilik tarihi geçmiş. */
  expired: boolean
  /** Teklif siparişe dönüştürüldüyse sipariş kimliği. */
  orderId: string | null
  /** Süreç: ayrıntılı durum, revizyon, onaylı revizyon, proje, kalem fiyatı görünürlüğü, iskonto kademesi. */
  flowStatus: QuoteFlowStatus
  revision: number
  currentApproved: boolean
  isFinal: boolean
  projectId: string
  showLinePrices: boolean
  discountTier: 'staff' | 'manager' | 'owner'
  effectiveDiscount: string
}

export type OfferSummary = { acceptedTotal: Money; openTotal: Money; openCount: number; expiredCount: number; acceptanceRate: number | null }

/** Sipariş onayında girilen lisans bilgileri. */
export type License = { branchLimit: number; term: LicenseTerm; validUntil: string; maintenanceUntil: string; supportUntil: string }

/** Kabul edilen tekliften oluşur; kalemler ve tutarlar dönüşüm anında sabitlenir. */
export type Order = {
  id: string
  customerId: string
  offerId: string
  offerNumber: string
  number: string
  createdAt: string
  createdBy: string
  status: OrderStatus
  items: OfferItem[]
  lineTotals: Record<string, Money>
  subtotal: Money
  discount: Money
  total: Money
  license: License | null
  decision: { by: string; at: string; note: string } | null
}

export type Renewal = {
  id: string
  customerId: string
  orderId: string | null
  kind: RenewalKind
  dueOn: string
  amount: Money
  status: RenewalStatus
  notes: string
  /** Sunucu hesaplar: bugüne göre kalan gün (geçmişse negatif). */
  daysLeft: number
}

export type Followup = {
  id: string
  customerId: string
  channel: Channel
  dueOn: string
  ownerId: string
  note: string
  done: boolean
  doneAt: string | null
  createdAt: string
  /** Sunucu hesaplar. */
  overdue: boolean
}

export type CustomerModule = { id: string; customerId: string; moduleId: string; name: string; licenseType: 'one_time' | 'monthly' | 'yearly'; startDate: string; status: ModuleStatus; price: Money }

export type WorkRecord = { id: string; customerId: string; kind: WorkKind; date: string; staffId: string; status: WorkStatus; note: string }

export type Invoice = { id: string; customerId: string; number: string; date: string; amount: Money; fileName: string; collectionId: string | null; uploadedBy: string; uploadedAt: string }

export type Receipt = { id: string; customerId: string; collectionId: string; date: string; amount: Money; reference: string; fileName: string }

export type Collection = {
  id: string
  customerId: string
  code: string
  dueDate: string
  amount: Money
  description: string
  /** Sunucu hesaplar. */
  paid: Money
  remaining: Money
  status: CollectionStatus
}

export type RequestMessage = { id: string; author: string; body: string; at: string }
export type SupportRequest = {
  id: string
  customerId: string
  number: string
  title: string
  description: string
  /** İlgili modül (müşterinin modüllerinden); boş olabilir. */
  module: string
  priority: Priority
  status: RequestStatus
  createdAt: string
  assigneeId: string | null
  messages: RequestMessage[]
}

/** Deneme lisansı: belirli modüller, belirli gün sayısı; siparişe bağlanınca gerçek lisansa döner. */
export type Trial = {
  id: string
  customerId: string
  modules: string[]
  startedOn: string
  endsOn: string
  /** Sunucu hesaplar: aktif ama bitişi geçmişse `expired`. */
  status: TrialStatus
  notes: string
  extensions: { id: string; days: number; reason: string; by: string; at: string }[]
  orderId: string | null
  createdBy: string
  /** Sunucu hesaplar. */
  daysLeft: number
}
export type TrialInput = { customerId: string; modules: string[]; days: number; notes: string; assignments?: Record<string, string> }

export type EmailLog = { id: string; customerId: string; to: string; subject: string; body: string; sentAt: string; by: string }

export type CrmFile = {
  id: string
  customerId: string
  category: FileCategory
  title: string
  description: string
  fileName: string
  size: number
  visibility: 'team' | 'all'
  meetingId: string | null
  uploadedAt: string
  uploadedBy: string
  linked: boolean
  unlinkReason: string
}

export type CustomerSummary = { contract: Money; collected: Money; remaining: Money; collectedPct: number; openRequests: number }

export type TimelineItem = { id: string; at: string; kind: 'meeting' | 'offer' | 'work' | 'receipt' | 'request' | 'email'; title: string; by: string; detail: string; badge?: string; refId: string }

export type CrmOverview = {
  counts: Record<Stage, number>
  pendingCollection: Money
  funnel: { stage: Exclude<Stage, 'lost'>; count: number; pct: number }[]
  followUps: { customerId: string; date: string; text: string; kind: 'call' | 'meeting' | 'collection'; overdueDays: number }[]
}

export type CustomerInput = Omit<Customer, 'id' | 'createdAt' | 'lastMeeting' | 'nextAction' | 'balance' | 'categoryId' | 'sectorId'>
export type MeetingInput = Omit<Meeting, 'id' | 'expenses' | 'notes' | 'history' | 'minutes' | 'ownerId' | 'agenda' | 'guests' | 'online' | 'restricted'>
export type OfferInput = Omit<Offer, 'id' | 'subtotal' | 'discount' | 'total' | 'number' | 'lineTotals' | 'expired' | 'orderId' | 'flowStatus' | 'revision' | 'currentApproved' | 'isFinal' | 'projectId' | 'showLinePrices' | 'discountTier' | 'effectiveDiscount'>
export type ModuleInput = Pick<CustomerModule, 'customerId' | 'moduleId' | 'startDate' | 'status' | 'price'>
export type WorkInput = Omit<WorkRecord, 'id'>
export type InvoiceInput = Omit<Invoice, 'id' | 'uploadedBy' | 'uploadedAt'>
export type ReceiptInput = Omit<Receipt, 'id'>
export type RenewalInput = Omit<Renewal, 'id' | 'daysLeft'>
export type FollowupInput = Pick<Followup, 'customerId' | 'channel' | 'dueOn' | 'ownerId' | 'note'>
export type CollectionInput = Pick<Collection, 'customerId' | 'dueDate' | 'amount' | 'description'>
export type RequestInput = Omit<SupportRequest, 'id' | 'number' | 'createdAt' | 'messages'>
export type EmailInput = Pick<EmailLog, 'customerId' | 'to' | 'subject' | 'body'>
export type FileInput = Pick<CrmFile, 'customerId' | 'category' | 'title' | 'description' | 'fileName' | 'size' | 'visibility' | 'meetingId'>
